import { logger } from '@fe/core';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 } from 'uuid';
import { ExtractKeysExtend } from '../../../components/helpers/helpers';
import { staticRouteColors } from '../../../components/route-analyzer/static-colors';
import { AnalyzerRoute } from '../../../components/route-analyzer/types';
import routeApi from '../../../services/http/route-api';
import { addNotification, NotificationType } from './global-slice';
import { type RootState } from './index';

export type NewRouteModel = Omit<AnalyzerRoute, 'id' | 'color'> & { color?: string };

export interface RouteAnalyzerSlice {
    routes: AnalyzerRoute[];
    settings: {
        drawLabels: boolean;
        drawWaypoints: boolean;
        drawRings: boolean;
    };
    busy: boolean;
    autogeneratedColorIndex: number;
}

const defaultState: RouteAnalyzerSlice = {
    routes: [],
    settings: {
        drawLabels: true,
        drawWaypoints: true,
        drawRings: true
    },
    autogeneratedColorIndex: 0,
    busy: false
};

export const routeAnalyzerSlice = createSlice({
    name: 'routeAnalyzer',
    initialState: defaultState,
    reducers: {
        routeAnalyzerRemoveRoute: (state, params: PayloadAction<{ id: string }>) => {
            state.routes = state.routes.filter((x) => x.id !== params.payload.id);
        },
        remoteAnalyzerRemoveAllRoutes: (state) => {
            state.routes = [];
            state.autogeneratedColorIndex = 0;
        },
        routeAnalyzerUpdateSetting: (
            state,
            params: PayloadAction<{ key: ExtractKeysExtend<RouteAnalyzerSlice['settings'], boolean>; value: boolean }>
        ) => {
            state.settings[params.payload.key] = params.payload.value;
        },
        routeAnalyzerSetAllRoutesVisible: (state, params: PayloadAction<boolean>) => {
            state.routes.forEach((x) => (x.visible = params.payload));
        }
    },
    extraReducers: (builder) => {
        builder.addCase(routeAnalyzerAddRoutes.fulfilled, (state, action) => {
            if (action.meta.arg.beforeId) {
                const idx = state.routes.findIndex((x) => x.id === action.meta.arg.beforeId);
                if (idx > -1) {
                    state.routes = [
                        ...state.routes.slice(0, idx + 1),
                        ...action.payload.map((x) => ({
                            ...x,
                            id: v4(),
                            color: x.color ?? staticRouteColors[state.autogeneratedColorIndex++ % staticRouteColors.length]
                        })),
                        ...state.routes.slice(idx + 1)
                    ];
                }
            } else {
                if (action.meta.arg.replace) {
                    state.routes = [
                        ...action.payload.map((x) => ({
                            ...x,
                            id: v4(),
                            color: x.color ?? staticRouteColors[state.autogeneratedColorIndex++ % staticRouteColors.length]
                        }))
                    ];
                } else {
                    state.routes.push(
                        ...action.payload.map((x) => ({
                            ...x,
                            id: v4(),
                            color: x.color ?? staticRouteColors[state.autogeneratedColorIndex++ % staticRouteColors.length]
                        }))
                    );
                }
            }
            state.busy = false;
        });

        builder.addCase(routeAnalyzerAddRoutes.pending, (state) => {
            state.busy = true;
        });

        builder.addCase(routeAnalyzerUpdateRoute.fulfilled, (state, action) => {
            const idx = state.routes.findIndex((x) => x.id === action.payload.route.id);
            if (idx > -1) {
                state.routes[idx] = {
                    ...state.routes[idx],
                    ...action.payload.route
                };
            }
        });
    }
});

// Update async thunk
export const routeAnalyzerUpdateRoute = createAsyncThunk(
    'route-analyzer/update-route',
    async (params: { route: Pick<AnalyzerRoute, 'id'> & Partial<Omit<AnalyzerRoute, 'id'>> }, api) => {
        const existingRoute = api.getState() as RootState;
        const route = existingRoute.routeAnalyzer.routes.find((x) => x.id === params.route.id);
        if (!route) {
            return api.rejectWithValue('Route not found');
        }

        // If there's an update to the route string and its different than the previous route string call decode api
        if (params.route.route && params.route.route !== route.route) {
            const response = await routeApi.decodeRoute(params.route.route);
            if (response?.routes?.[0]?.wp) {
                return api.fulfillWithValue({
                    route: {
                        ...params.route,
                        waypoints: {
                            items: response.routes[0].wp
                        }
                    }
                });
            } else {
                api.dispatch(addNotification({ type: NotificationType.routeDecodeError }));
            }
        }

        return { route: params.route };
    }
);

export const routeAnalyzerAddRoutes = createAsyncThunk(
    'route-analyzer/add-route',
    async (params: { routes: NewRouteModel[]; beforeId?: string; replace?: boolean }, api) => {
        const notDecoded = params.routes.filter((x) => !x.waypoints);
        if (notDecoded.length > 0) {
            if (notDecoded.length > 50) {
                return api.fulfillWithValue(params.routes);
            }

            try {
                const response = await routeApi.decodeRoute(...params.routes.map((x) => x.route));
                if (response?.routes) {
                    for (let i = 0; i < response.routes.length; i++) {
                        notDecoded[i].waypoints = { items: response.routes[i].wp };
                    }
                }

                return api.fulfillWithValue(params.routes);
            } catch (err) {
                logger.trackException(err);
            }
        }

        return api.fulfillWithValue(params.routes);
    }
);

export const { routeAnalyzerRemoveRoute, remoteAnalyzerRemoveAllRoutes, routeAnalyzerUpdateSetting, routeAnalyzerSetAllRoutesVisible } =
    routeAnalyzerSlice.actions;
